import type { TzMode } from "@app/date";
import type { FreezerActivity } from "@app/freezer";
import {
  type ColumnOrderState,
  type ColumnSizingState,
  type Table,
  type Updater,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { LoadingSpinner } from "../loading";
import { ACTIVITY_COLUMNS, type ActivityTableMeta } from "./activity-columns";
import { useTruncationDetection } from "./use-truncation-detection";

const MAX_INITIAL_FRACTION = 0.3;
const MIN_COL_SIZE = 40;
const DEFAULT_FIXED_COL_SIZE = 56;
const DEFAULT_COL_SIZE = 150;

function computeAutoColumnSizes(
  containerWidth: number,
  manuallyResized: Set<string>,
  currentSizing: ColumnSizingState,
  columnVisibility?: Record<string, boolean>,
): ColumnSizingState {
  if (containerWidth <= 0) return currentSizing;

  const maxInitialSize = containerWidth * MAX_INITIAL_FRACTION;
  const newSizing: ColumnSizingState = {};

  let availableWidth = containerWidth;
  const autoColumns: { id: string; weight: number; minSize: number }[] = [];

  for (const col of ACTIVITY_COLUMNS) {
    const id = col.id as string;
    if (!id) continue;

    if (columnVisibility && columnVisibility[id] === false) continue;

    if (col.enableResizing === false) {
      availableWidth -= col.size ?? DEFAULT_FIXED_COL_SIZE;
    } else if (manuallyResized.has(id)) {
      const size = currentSizing[id] ?? col.size ?? DEFAULT_COL_SIZE;
      newSizing[id] = size;
      availableWidth -= size;
    } else {
      autoColumns.push({
        id,
        weight: col.size ?? DEFAULT_COL_SIZE,
        minSize: col.minSize ?? MIN_COL_SIZE,
      });
    }
  }

  if (autoColumns.length === 0) {
    return newSizing;
  }

  if (availableWidth <= 0) {
    for (const col of autoColumns) {
      newSizing[col.id] = col.minSize;
    }
    return newSizing;
  }

  const totalWeight = autoColumns.reduce((sum, c) => sum + c.weight, 0);

  const sizes: Record<string, number> = {};
  const constrained: string[] = [];
  const unconstrained: string[] = [];
  let constrainedTotal = 0;

  for (const col of autoColumns) {
    const proportional = (col.weight / totalWeight) * availableWidth;
    if (proportional < col.minSize) {
      sizes[col.id] = col.minSize;
      constrained.push(col.id);
      constrainedTotal += col.minSize;
    } else if (proportional > maxInitialSize) {
      sizes[col.id] = maxInitialSize;
      constrained.push(col.id);
      constrainedTotal += maxInitialSize;
    } else {
      sizes[col.id] = proportional;
      unconstrained.push(col.id);
    }
  }

  if (constrained.length > 0 && unconstrained.length > 0) {
    const remaining = availableWidth - constrainedTotal;
    const unconstrainedWeight = autoColumns
      .filter((c) => unconstrained.includes(c.id))
      .reduce((sum, c) => sum + c.weight, 0);

    for (const id of unconstrained) {
      const col = autoColumns.find((c) => c.id === id);
      if (!col) continue;
      sizes[id] = Math.max(
        col.minSize,
        Math.min(
          (col.weight / unconstrainedWeight) * remaining,
          maxInitialSize,
        ),
      );
    }
  }

  for (const [id, size] of Object.entries(sizes)) {
    newSizing[id] = size;
  }

  return newSizing;
}

function HeaderCellContent({ children }: { children: ReactNode }) {
  const { ref, isTruncated } = useTruncationDetection("width");

  return (
    <div className="relative group/header min-w-0">
      <div ref={ref} className="truncate">
        {children}
      </div>
      {isTruncated && (
        <div className="hidden group-hover/header:block absolute top-full left-0 mt-1 px-2 py-1 bg-black/85 text-white text-sm font-normal rounded whitespace-nowrap z-50">
          {children}
        </div>
      )}
    </div>
  );
}

interface ActivityDataTableProps {
  activities: FreezerActivity[];
  isLoading: boolean;
  tz: TzMode;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  hasPendingChanges?: boolean;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (order: ColumnOrderState) => void;
  q?: string;
}

export function ActivityDataTable({
  activities,
  isLoading,
  tz,
  columnVisibility,
  onColumnVisibilityChange,
  hasPendingChanges = false,
  columnOrder,
  onColumnOrderChange,
  q,
}: ActivityDataTableProps) {
  const meta: ActivityTableMeta = { tz, q };
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<Table<FreezerActivity> | null>(null);
  const manuallyResizedRef = useRef(new Set<string>());

  const [containerWidth, setContainerWidth] = useState(0);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setContainerWidth(width);
    });

    observer.observe(el);
    setContainerWidth(el.clientWidth);

    const remeasure = () => {
      setContainerWidth(el.clientWidth);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        remeasure();
      }
    };

    window.addEventListener("resize", remeasure);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", remeasure);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const visibilityKey = columnVisibility
    ? Object.entries(columnVisibility)
        .filter(([, v]) => v)
        .map(([k]) => k)
        .sort()
        .join(",")
    : "";

  useEffect(() => {
    if (containerWidth <= 0) return;
    setColumnSizing((prev) =>
      computeAutoColumnSizes(
        containerWidth,
        manuallyResizedRef.current,
        prev,
        columnVisibility,
      ),
    );
  }, [containerWidth, visibilityKey]);

  const handleColumnSizingChange = useCallback(
    (updater: Updater<ColumnSizingState>) => {
      const resizingColumn =
        tableRef.current?.getState().columnSizingInfo.isResizingColumn;
      if (resizingColumn) {
        manuallyResizedRef.current.add(resizingColumn);
      }
      setColumnSizing((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
    },
    [],
  );

  const table = useReactTable({
    data: activities,
    columns: ACTIVITY_COLUMNS,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    meta,
    state: {
      columnSizing,
      ...(columnVisibility ? { columnVisibility } : {}),
      ...(columnOrder ? { columnOrder } : {}),
    },
    onColumnSizingChange: handleColumnSizingChange,
    onColumnVisibilityChange: onColumnVisibilityChange
      ? (updater) => {
          const next =
            typeof updater === "function"
              ? updater(columnVisibility ?? {})
              : updater;
          onColumnVisibilityChange(next);
        }
      : undefined,
    onColumnOrderChange: onColumnOrderChange
      ? (updater) => {
          const next =
            typeof updater === "function"
              ? updater(columnOrder ?? [])
              : updater;
          onColumnOrderChange(next);
        }
      : undefined,
  });
  tableRef.current = table;

  const totalSize = table.getCenterTotalSize();
  const tableWidth = Math.max(containerWidth, totalSize);

  return (
    <div
      ref={containerRef}
      className="overflow-x-auto w-full shadow ring-1 ring-black ring-opacity-5 rounded-lg overscroll-x-contain"
    >
      <table
        className="divide-y divide-gray-300"
        style={{
          width: tableWidth,
          tableLayout: "fixed",
        }}
      >
        <thead className="bg-gray-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className={`relative pl-4 pr-2 py-3 text-sm font-normal text-gray-500 align-top text-left ${
                    header.column.getCanResize() ? "hover:bg-gray-100" : ""
                  }`}
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder ? null : (
                    <HeaderCellContent>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </HeaderCellContent>
                  )}
                  {header.column.getCanResize() ? (
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      onDoubleClick={() => header.column.resetSize()}
                      className={`absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none ${
                        header.column.getIsResizing()
                          ? "bg-indigo-600 opacity-100"
                          : "bg-gray-400 opacity-0 hover:opacity-100"
                      }`}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody className="divide-y divide-gray-200 bg-white">
          {isLoading && activities.length === 0 ? (
            <tr>
              <td
                colSpan={table.getVisibleLeafColumns().length}
                className="text-sm text-gray-500 pl-4 py-0 text-left"
              >
                <div className="flex min-h-[200px] items-center justify-center">
                  <LoadingSpinner color="#4F46E5" />
                </div>
              </td>
            </tr>
          ) : activities.length === 0 ? (
            <tr>
              <td
                colSpan={table.getVisibleLeafColumns().length}
                className="text-sm text-gray-500 pl-4 py-0 text-left"
              >
                <div className="flex min-h-[200px] flex-col items-center justify-center text-center text-base leading-relaxed text-gray-600">
                  <div className="font-semibold text-gray-900">
                    No activities found matching the applied filters.
                  </div>
                  {hasPendingChanges ? (
                    <div>
                      You have unapplied filter changes. Click "Apply Filters"
                      to get updated results.
                    </div>
                  ) : (
                    <div>Try adjusting your filters for a broader search.</div>
                  )}
                </div>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={`${row.id}-${q || ""}`}
                className="group hover:bg-gray-50"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="text-sm text-gray-500 pl-4 pr-2 py-3 text-left"
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
